import {
  Box,
  Button,
  Divider,
  IconButton,
  IconButtonProps,
  PlusIcon,
  Spinner,
  Text,
  TrashIcon,
} from '@razorpay/blade/components';
import { format, parse } from 'date-fns';
import React, { useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { dateFormats } from 'utils/Dates';
import { windowTypeNames } from '../constants';
import { AdvancedWindow, NewAdvancedWindow } from '../types';
import { Table } from './styles';
import useAdvancedWindowConfigQuery from './useAdvancedWindowConfigQuery';
import CreateCustomWindowModal from '../../../ui/CustomWindow/CreateCustomWindowModal';
import DeleteConfirmation from 'components/ui/CustomWindow/DeleteConfirmation';
import EmployeeListing from 'components/ui/CustomWindow/EmployeeListing';
import { OverrideEmployeeListing } from 'components/ui/CustomWindow/OverrideEmployeeListing';
import { CreateCustomWindowModalV2 } from 'components/ui/CustomWindow/CreateCustomWindowModalV2';
import { EmployeeOption } from 'components/ui/CustomWindow/types';
import flexibleBenefitsApi from 'api/gql-utils/flexibleBenefits';
import {
  CreateOverrideInput,
  SettingOverride,
} from 'api/gql-utils/flexibleBenefits/flexibleBenefitsSettings/types';
import { NewCustomWindow } from 'components/ui/CustomWindow/types';
import api from 'api';
import OverridesTable from './OverridesTable';
import { showToastViaEvent } from 'utils/ToastEvents';
import useIsFeatureDisabledForRearch from 'access/useIsFeatureDisabledForRearch';
import { TableRowData, TableRowProps } from '../types';

const TableRow = ({
  type,
  startDate,
  endDate,
  employees,
  onDelete,
  onEmployeeCountClick,
}: TableRowProps) => {
  return (
    <tr>
      <td>
        <Text color="surface.text.gray.muted">
          {windowTypeNames[type as keyof typeof windowTypeNames]}
        </Text>
      </td>
      <td>
        <Text color="surface.text.gray.muted">
          {format(
            parse(startDate, dateFormats.yearMonthDate, new Date()),
            dateFormats.dateMonthYear,
          )}
        </Text>
      </td>
      <td>
        <Text color="surface.text.gray.muted">
          {format(parse(endDate, dateFormats.yearMonthDate, new Date()), dateFormats.dateMonthYear)}
        </Text>
      </td>
      <td style={{ cursor: 'pointer' }} onClick={onEmployeeCountClick}>
        <Text color="surface.text.primary.normal">{employees.length || 0}</Text>
      </td>
      <td>
        <Box display="flex" gap="spacing.5" justifyContent="flex-end">
          <IconButton icon={TrashIcon} accessibilityLabel="Delete" onClick={onDelete} />
        </Box>
      </td>
    </tr>
  );
};

const CustomWindowSettings = ({ orgId }: { orgId: string | number }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
  const [isEmployeeListOpen, setIsEmployeeListOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<TableRowData | null>(null);
  const [isOverrideEmployeeListOpen, setIsOverrideEmployeeListOpen] = useState(false);
  const [selectedOverrideId, setSelectedOverrideId] = useState<string | null>(null);
  const [isOverrideDeleteConfirmationOpen, setIsOverrideDeleteConfirmationOpen] = useState(false);
  const [selectedOverrideIdForDeletion, setSelectedOverrideIdForDeletion] = useState<string | null>(
    null,
  );
  const [overrideEmployeeIdList, setOverrideEmployeeIdLists] = useState<string[]>([]);

  const { isM3S2Enabled } = useIsFeatureDisabledForRearch();

  const { advancedWindowItems, deleteAdvancedWindowConfig, addAdvancedWindowConfig, isLoading } =
    useAdvancedWindowConfigQuery({
      orgId,
      onAddSuccess: () => {
        setIsOpen(false);
      },
      onDeleteSuccess: () => {
        setIsDeleteConfirmationOpen(false);
      },
      onUpdateSuccess: () => {},
    });

  const listDeclarationOverridesQuery = useQuery({
    queryKey: ['list-overrides', 'declaration_window_config', orgId],
    queryFn: () =>
      flexibleBenefitsApi.queries.listOverrides({
        setting_name: 'declaration_window_config',
        page: 1,
        page_size: 100,
      }),
    enabled: isM3S2Enabled,
  });

  const listProofUploadOverridesQuery = useQuery({
    queryKey: ['list-overrides', 'proof_upload_window_config', orgId],
    queryFn: () =>
      flexibleBenefitsApi.queries.listOverrides({
        setting_name: 'proof_upload_window_config',
        page: 1,
        page_size: 100,
      }),
    enabled: isM3S2Enabled,
  });

  const createOverrideMutation = useMutation(
    (input: CreateOverrideInput) => flexibleBenefitsApi.mutations.createOverride(input),
    {
      onSuccess: (data) => {
        showToastViaEvent({
          text: data?.payrollCreateOverride?.data?.message || 'Override created successfully!',
          timeout: 3000,
          type: 'success',
          icon: 'success',
        });
        setIsOpen(false);
        listDeclarationOverridesQuery.refetch();
        listProofUploadOverridesQuery.refetch();
      },
      onError: (error) => {
        showToastViaEvent({
          text: 'Failed to create override!',
          timeout: 3000,
          type: 'danger',
          icon: 'info',
        });
      },
    },
  );

  const deleteOverrideMutation = useMutation(
    (overrideId: string) =>
      flexibleBenefitsApi.mutations.deleteOverride({ override_id: overrideId }),
    {
      onSuccess: (data) => {
        const responseData = data?.payrollDeleteOverride?.data;
        listDeclarationOverridesQuery.refetch();
        listProofUploadOverridesQuery.refetch();
        setIsOverrideDeleteConfirmationOpen(false);
        setSelectedOverrideIdForDeletion(null);
        showToastViaEvent({
          text: responseData?.message || 'Override deleted successfully!',
          timeout: 3000,
          type: 'success',
          icon: 'success',
        });
      },
      onError: (error) => {
        showToastViaEvent({
          text: 'Failed to delete override!',
          timeout: 3000,
          type: 'danger',
          icon: 'info',
        });
      },
    },
  );

  const employeesQuery = useQuery({
    queryKey: ['employees-for-override', orgId],
    queryFn: () =>
      api.employeeOnboardingGql.queries.getPeopleList({
        page: 1,
        pageSize: 1000,
        type: 'employee',
        isActive: true,
      }),
    enabled: isM3S2Enabled && isOpen,
  });

  const overrideEmployeesQuery = useQuery({
    queryKey: ['override-employees', selectedOverrideId],
    queryFn: () =>
      flexibleBenefitsApi.queries.getEmployeesForOverride({
        override_id: selectedOverrideId!,
        page: 1,
        page_size: 1000,
      }),
    onSuccess: (data) => {
      setOverrideEmployeeIdLists(data?.payrollGetEmployeesForOverride?.employee_ids || []);
    },
    enabled: !!selectedOverrideId && isOverrideEmployeeListOpen,
  });

  const {
    data: employeeData,
    isFetching: isFetchingEmployees,
    isLoading: isLoadingEmployees,
    isError,
    refetch,
    error,
  } = useQuery({
    queryKey: [overrideEmployeeIdList, 'list-employee-data', selectedOverrideId],
    queryFn: async () => {
      const result = await api.userQueries.getUserByPeopleIds({
        input: {
          userIds: overrideEmployeeIdList,
        },
      });

      return result.payrollGetUsersByPeopleIds.data;
    },
    enabled: !overrideEmployeesQuery.isFetching && overrideEmployeeIdList.length > 0,
  });

  const employeeOptions: EmployeeOption[] =
    employeesQuery.data?.payrollFetchEmployees?.data?.users?.map((user) => ({
      id: user.id,
      name: user.name,
      email: user.email,
      employeeId: user.employeeId,
      designation: user.designation,
      department: user.department,
    })) || [];

  const mergedOverrides: SettingOverride[] = [
    ...(listDeclarationOverridesQuery.data?.payrollListOverrides?.overrides || []),
    ...(listProofUploadOverridesQuery.data?.payrollListOverrides?.overrides || []),
  ].filter((override) => !override.is_deleted);

  const groupedItems = advancedWindowItems.reduce((a, c) => {
    const key = c.startDate + c.endDate + c.type;
    const arr = a[key] && a[key].employees ? [...a[key].employees, c] : [c];
    return {
      ...a,
      [key]: {
        startDate: c.startDate,
        endDate: c.endDate,
        type: c.type,
        employees: arr,
      },
    };
  }, {} as Record<string, { startDate: string; endDate: string; type: string; employees: Array<AdvancedWindow> }>);

  const onDismiss = () => {
    setIsOpen(false);
  };

  const onDeleteConfirmationDismiss = () => {
    setIsDeleteConfirmationOpen(false);
  };

  const onEmployeeListingDismiss = () => {
    setIsEmployeeListOpen(false);
  };

  const handleCreateNewWindowClick = () => {
    setIsOpen(true);
  };

  const onDeleteConfirmation = () => {
    if (selectedRow) {
      deleteAdvancedWindowConfig.mutate({
        org: orgId,
        ids: selectedRow.employees.map((e) => e.id),
      });
    }
  };

  const handleEmployeeCountClick = (id: string) => {
    setSelectedRow(groupedItems[id]);
    setIsEmployeeListOpen(true);
  };

  const handleDeleteClick = (id: string) => {
    setSelectedRow(groupedItems[id]);
    setIsDeleteConfirmationOpen(true);
  };

  const handleOverrideDeleteClick = (overrideId: string) => {
    setSelectedOverrideIdForDeletion(overrideId);
    setIsOverrideDeleteConfirmationOpen(true);
  };

  const onOverrideDeleteConfirmation = () => {
    if (selectedOverrideIdForDeletion) {
      deleteOverrideMutation.mutate(selectedOverrideIdForDeletion);
    }
  };

  const onOverrideDeleteConfirmationDismiss = () => {
    setIsOverrideDeleteConfirmationOpen(false);
    setSelectedOverrideIdForDeletion(null);
  };

  const handleOverrideEmployeeCountClick = (overrideId: string) => {
    setSelectedOverrideId(overrideId);
    setIsOverrideEmployeeListOpen(true);
  };

  // const handleAddEmployeeToOverride = (overrideId: string) => {
  //   setSelectedOverrideId(overrideId);
  //   setIsOverrideEmployeeListOpen(true);
  // };

  // const handleRemoveEmployeeFromOverride = (overrideId: string) => {
  //   setSelectedOverrideId(overrideId);
  //   setIsOverrideEmployeeListOpen(true);
  // };

  const isTableVisible = advancedWindowItems.length > 0;

  const onCreateNewWindow = (window: NewAdvancedWindow) => {
    addAdvancedWindowConfig.mutate({
      data: {
        employeeEmails: window.employees.split(',').filter((e) => e.trim().length > 0),
        endDate: format(window.endDate, dateFormats.yearMonthDate),
        startDate: format(window.startDate, dateFormats.yearMonthDate),
        type: window.type,
      },
      org: orgId,
    });
  };

  const onCreateNewWindowV2 = (window: NewCustomWindow) => {
    const validFromTimestamp = Math.floor(window.startDate.getTime() / 1000);
    const validToTimestamp = Math.floor(window.endDate.getTime() / 1000);
    const validFrom = String(validFromTimestamp);
    const validTo = String(validToTimestamp);

    const settingName =
      window.type === 'declaration_window'
        ? 'declaration_window_config'
        : 'proof_upload_window_config';

    const employeeIds = window.employees.split(',').map((id) => id.trim());

    const input: CreateOverrideInput = {
      setting_name: settingName,
      valid_from: validFrom,
      valid_to: validTo,
      map_value: {
        values: {
          custom_range: {
            date_ranges_value: {
              values: [
                {
                  start: validFrom,
                  end: validTo,
                },
              ],
            },
          },
        },
      },
      employee_ids: employeeIds,
    };

    createOverrideMutation.mutate(input);
  };

  if (isLoading) {
    return (
      <Box display="grid" placeItems="center" marginTop="42px">
        <Spinner accessibilityLabel="Loading custom window settings" />
      </Box>
    );
  }

  const isOverRideListLoading =
    listDeclarationOverridesQuery.isLoading || listProofUploadOverridesQuery.isLoading;

  return (
    <Box marginTop="42px">
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box display="flex" flexDirection="column" gap="spacing.2" maxWidth="300px">
          <Text size="large">Advance window configuration</Text>
          <Text size="small" color="surface.text.gray.muted">
            Custom windows opened for employees which are currently active
          </Text>
        </Box>
        <Button
          size="small"
          variant="secondary"
          icon={PlusIcon}
          onClick={handleCreateNewWindowClick}>
          Create new window
        </Button>
      </Box>
      {isM3S2Enabled ? (
        <OverridesTable
          overrides={mergedOverrides}
          isLoading={isOverRideListLoading}
          onDeleteOverride={handleOverrideDeleteClick}
          onEmployeeCountClick={handleOverrideEmployeeCountClick}
        />
      ) : isTableVisible ? (
        <Box
          marginTop={'spacing.7'}
          borderColor={'surface.border.gray.subtle'}
          borderWidth="thin"
          borderRadius={'medium'}
          padding={'spacing.7'}
          zIndex="1"
          backgroundColor="surface.background.gray.moderate">
          <Table>
            <thead>
              <tr>
                <th>
                  <Text weight="semibold">Window</Text>
                </th>
                <th align="left">
                  <Text weight="semibold">From</Text>
                </th>

                <th align="left">
                  <Text weight="semibold">To</Text>
                </th>
                <th>
                  <Text weight="semibold">Employee(s)</Text>
                </th>
                <th>
                  <Box />
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td colSpan={5}>
                  <Divider />
                </td>
              </tr>
              {Object.entries(groupedItems).map(([key, row]) => (
                <TableRow
                  key={key}
                  id={key}
                  type={row.type}
                  startDate={row.startDate}
                  endDate={row.endDate}
                  employees={row.employees ?? []}
                  onDelete={() => handleDeleteClick(key)}
                  onEdit={() => null}
                  onEmployeeCountClick={() => handleEmployeeCountClick(key)}
                />
              ))}
            </tbody>
          </Table>
        </Box>
      ) : null}
      {isM3S2Enabled ? (
        <CreateCustomWindowModalV2
          isOpen={isOpen}
          onDismiss={onDismiss}
          onSubmit={onCreateNewWindowV2}
          isLoading={createOverrideMutation.isLoading}
          employees={employeeOptions}
          isLoadingEmployees={employeesQuery.isLoading}
        />
      ) : (
        <CreateCustomWindowModal
          isOpen={isOpen}
          onDismiss={onDismiss}
          onSubmit={onCreateNewWindow}
          isLoading={addAdvancedWindowConfig.isLoading}
        />
      )}
      <DeleteConfirmation
        isOpen={isDeleteConfirmationOpen}
        onDismiss={onDeleteConfirmationDismiss}
        onConfirm={onDeleteConfirmation}
        isLoading={deleteAdvancedWindowConfig.isLoading}
      />
      <DeleteConfirmation
        isOpen={isOverrideDeleteConfirmationOpen}
        onDismiss={onOverrideDeleteConfirmationDismiss}
        onConfirm={onOverrideDeleteConfirmation}
        isLoading={deleteOverrideMutation.isLoading}
      />
      <EmployeeListing
        employees={selectedRow?.employees || []}
        isOpen={isEmployeeListOpen}
        onDismiss={onEmployeeListingDismiss}
      />
      <OverrideEmployeeListing
        employees={employeeData?.employees ?? []}
        isOpen={isOverrideEmployeeListOpen}
        isLoading={overrideEmployeesQuery.isLoading || isLoadingEmployees}
        isFetching={overrideEmployeesQuery.isFetching || isFetchingEmployees}
        onDismiss={() => {
          setIsOverrideEmployeeListOpen(false);
          setSelectedOverrideId(null);
        }}
      />
    </Box>
  );
};

export default CustomWindowSettings;
